/**
 * /practice/customize — Per-stop item count editor.
 *
 * Arrived at via "Customize →" on the hub. Template and duration are already
 * set there; this page is purely for tuning item counts per stop for this session.
 */

import { type ReactNode, useState, useMemo, useCallback, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  MVP_NODES,
  EXERCISE_SKILL_NODES,
  resolveNodes,
  currentRetrievability,
  type CurriculumNode,
  type Item,
  type CardStore,
  type CoachPlan,
  type PlanStop,
  type PlanStopNodeAcquisition,
  type WellKnownStopKind,
  type PlanStopKind,
} from "@music-app/learning-system";
import { hasLessonForNode } from "@music-app/lessons/runtime";
import { usePracticeData } from "../lib/use-review-data.js";
import { PageHeader } from "../components/PageHeader.js";
import { buttonClasses } from "../components/Button.js";

// ─── types ────────────────────────────────────────────────────────────────────

interface NodeEdit {
  nodeId: string;
  itemCount: number;
}

interface StopEdit {
  kind: PlanStop["kind"];
  title: string;
  reason: string;
  nodes: NodeEdit[];
  drillRoute?: string;
  nodeAcquisition?: readonly PlanStopNodeAcquisition[];
}

interface CustomState {
  stops: StopEdit[];
}

type FocusTrack = { track: string; label: string; nodes: CurriculumNode[] };

// ─── constants ────────────────────────────────────────────────────────────────

const STOP_LABELS: Record<WellKnownStopKind, string> = {
  strengthen:  "STRENGTHEN",
  review:      "REVIEW",
  introduce:   "INTRODUCE",
  maintenance: "KEEP SHARP",
};

const STOP_COLORS: Record<WellKnownStopKind, string> = {
  strengthen:  "text-warning",
  review:      "text-info",
  introduce:   "text-success",
  maintenance: "text-accent",
};

const STOP_DOT_BG: Record<WellKnownStopKind, string> = {
  strengthen:  "bg-warning",
  review:      "bg-info",
  introduce:   "bg-success",
  maintenance: "bg-accent",
};

const STOP_LEFT_RULE: Record<WellKnownStopKind, string> = {
  strengthen:  "border-l-4 border-l-warning",
  review:      "border-l-4 border-l-info",
  introduce:   "border-l-4 border-l-success",
  maintenance: "border-l-4 border-l-accent",
};

const STOP_KIND_ORDER: WellKnownStopKind[] = ["strengthen", "review", "introduce", "maintenance"];

function stopAttr<T>(dict: Record<WellKnownStopKind, T>, kind: PlanStopKind, fallback: T): T {
  return (dict as Record<string, T>)[kind] ?? fallback;
}

const STOP_EMPTY_REASON: Record<WellKnownStopKind, string> = {
  strengthen:  "no items with elevated lapse rate right now",
  review:      "no items are due for review right now",
  introduce:   "no new skills are eligible yet",
  maintenance: "no introduced items in this plan yet",
};

function nodeListenRoute(nodeId: string): string | null {
  for (const [skill, nodeIds] of Object.entries(EXERCISE_SKILL_NODES)) {
    if ((nodeIds as readonly string[]).includes(nodeId))
      return `/exercise/${skill}?mode=listen&nodeId=${nodeId}`;
  }
  return null;
}

// ─── plan resolution ──────────────────────────────────────────────────────────

function itemsForNode(
  nodeId: string,
  items: Item[],
  cards: CardStore,
  now: Date,
  excludeIds: Set<string>,
): Item[] {
  return items
    .filter((i) => i.nodeId === nodeId && !excludeIds.has(i.id))
    .map((i) => {
      const card = cards[i.id];
      return { item: i, r: card ? currentRetrievability(card, now) : 0 };
    })
    .sort((a, b) => a.r - b.r)
    .map((x) => x.item);
}

function resolveCustomPlan(
  stops: StopEdit[],
  items: Item[],
  cards: CardStore,
  now: Date,
): CoachPlan {
  const resolvedStops: PlanStop[] = [];

  for (const stopEdit of stops) {
    if (stopEdit.nodes.length === 0) continue;

    const excludeIds = new Set(resolvedStops.flatMap((s) => s.items.map((it) => it.id)));
    const resolvedItems: Item[] = [];

    for (const nodeEdit of stopEdit.nodes) {
      const usedIds = new Set([...excludeIds, ...resolvedItems.map((it) => it.id)]);
      const nodeItems = itemsForNode(nodeEdit.nodeId, items, cards, now, usedIds);
      resolvedItems.push(...nodeItems.slice(0, nodeEdit.itemCount));
    }

    if (resolvedItems.length === 0) continue;

    resolvedStops.push({
      kind: stopEdit.kind,
      title: stopEdit.title,
      reason: stopEdit.reason,
      drillRoute: stopEdit.drillRoute,
      itemCount: resolvedItems.length,
      estimatedMinutes: Math.max(1, Math.round(resolvedItems.length / 6)),
      items: resolvedItems,
    });
  }

  const totalItemCount = resolvedStops.reduce((s, st) => s + st.itemCount, 0);
  return {
    stops: resolvedStops,
    totalItemCount,
    totalEstimatedMinutes: Math.max(1, Math.round(totalItemCount / 6)),
    mode: "standard" as const,
  };
}

function stopsFromPlan(plan: CoachPlan): StopEdit[] {
  const byKind = new Map<PlanStop["kind"], StopEdit>();

  for (const s of plan.stops) {
    const nodeGroups = new Map<string, number>();
    for (const item of s.items) {
      nodeGroups.set(item.nodeId, (nodeGroups.get(item.nodeId) ?? 0) + 1);
    }
    byKind.set(s.kind, {
      kind: s.kind,
      title: s.title,
      reason: s.reason,
      drillRoute: s.drillRoute,
      nodeAcquisition: s.nodeAcquisition,
      nodes: Array.from(nodeGroups.entries()).map(([nodeId, itemCount]) => {
        const acq = s.nodeAcquisition?.find((a) => a.nodeId === nodeId);
        const resolvedCount =
          acq?.status === "needs-listen"
            ? Math.max(0, acq.threshold - acq.exposures)
            : itemCount;
        return { nodeId, itemCount: resolvedCount };
      }),
    });
  }

  return STOP_KIND_ORDER.map(
    (kind) =>
      byKind.get(kind) ?? {
        kind,
        title: stopAttr(STOP_LABELS, kind, kind.toUpperCase()),
        reason: "",
        nodes: [],
      },
  );
}

// ─── page ─────────────────────────────────────────────────────────────────────

export function ReviewCustomizePage(): ReactNode {
  const navigate = useNavigate();
  const location = useLocation();
  const passedPlan = (location.state as { plan?: CoachPlan } | null)?.plan ?? null;

  const {
    cards,
    cardsLoaded,
    items,
    sessionTargetMinutes: defaultMinutes,
    eligibleNewNodes,
    focusTracks,
    activePlan,
    generator,
    deps,
  } = usePracticeData();

  const now = useMemo(() => new Date(), []);

  // Restrict "add a skill" to nodes within the active plan's scope.
  const planScopedNodeIds = useMemo(() => {
    const allUnlocked = focusTracks.flatMap((t) => t.nodes);
    return new Set(resolveNodes(allUnlocked, activePlan).map((n) => n.id));
  }, [focusTracks, activePlan]);

  const planFocusTracks = useMemo(
    () => focusTracks
      .map((t) => ({ ...t, nodes: t.nodes.filter((n) => planScopedNodeIds.has(n.id)) }))
      .filter((t) => t.nodes.length > 0),
    [focusTracks, planScopedNodeIds],
  );

  const planEligibleNewNodes = useMemo(
    () => eligibleNewNodes.filter((n) => planScopedNodeIds.has(n.id)),
    [eligibleNewNodes, planScopedNodeIds],
  );

  const basePlan = useMemo(
    () => generator.build(deps, activePlan, defaultMinutes),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deps, activePlan, defaultMinutes, generator],
  );

  const [state, setState] = useState<CustomState>(() => ({
    stops: passedPlan ? stopsFromPlan(passedPlan) : [],
  }));

  // Seed from basePlan when navigated to directly (no passedPlan in state)
  const [seeded, setSeeded] = useState(() => passedPlan != null);
  useEffect(() => {
    if (!seeded && cardsLoaded && basePlan.stops.length > 0) {
      setState({ stops: stopsFromPlan(basePlan) });
      setSeeded(true);
    }
  }, [seeded, cardsLoaded, basePlan]);

  // ── plan summary ───────────────────────────────────────────────────────────
  const planSummary = useMemo(() => {
    const activeStops = state.stops.filter((s) => s.nodes.length > 0);
    const hasGatedStop = activeStops.some(
      (s) => s.kind === "introduce" && s.nodeAcquisition?.some((a) => a.status === "needs-listen"),
    );
    const totalItems = activeStops.reduce((sum, stop) => {
      return sum + stop.nodes.reduce((s, n) => {
        const acq = stop.nodeAcquisition?.find((a) => a.nodeId === n.nodeId);
        return s + (acq?.status === "needs-listen"
          ? Math.max(0, acq.threshold - acq.exposures)
          : n.itemCount);
      }, 0);
    }, 0);
    const totalMins = activeStops.reduce((sum, stop) => {
      if (stop.kind === "introduce") {
        const gatedNodes = stop.nodeAcquisition?.filter((a) => a.status === "needs-listen") ?? [];
        if (gatedNodes.length > 0) {
          const remaining = gatedNodes.reduce((s, a) => s + Math.max(0, a.threshold - a.exposures), 0);
          return sum + Math.max(1, Math.ceil(remaining / 6));
        }
      }
      const stopItems = stop.nodes.reduce((s, n) => s + n.itemCount, 0);
      return sum + Math.max(1, Math.round(stopItems / 6));
    }, 0);
    return { stopCount: activeStops.length, totalItems, totalMins, hasGatedStop };
  }, [state.stops]);

  // ── node editing ───────────────────────────────────────────────────────────
  const updateNode = useCallback((stopIdx: number, nodeIdx: number, count: number) => {
    setState((prev) => {
      const stops = [...prev.stops];
      const stop = { ...stops[stopIdx]! };
      const nodes = [...stop.nodes];
      nodes[nodeIdx] = { ...nodes[nodeIdx]!, itemCount: Math.max(1, count) };
      stops[stopIdx] = { ...stop, nodes };
      return { stops };
    });
  }, []);

  const removeNode = useCallback((stopIdx: number, nodeIdx: number) => {
    setState((prev) => {
      const stops = [...prev.stops];
      const stop = { ...stops[stopIdx]! };
      stops[stopIdx] = { ...stop, nodes: stop.nodes.filter((_, i) => i !== nodeIdx) };
      return { stops };
    });
  }, []);

  const addNode = useCallback((stopIdx: number, nodeId: string) => {
    setState((prev) => {
      const stops = [...prev.stops];
      const stop = { ...stops[stopIdx]! };
      if (stop.nodes.some((n) => n.nodeId === nodeId)) return prev;
      stops[stopIdx] = { ...stop, nodes: [...stop.nodes, { nodeId, itemCount: 6 }] };
      return { stops };
    });
  }, []);

  // ── resolved plan + start ──────────────────────────────────────────────────
  const resolvedPlan = useMemo(
    () => resolveCustomPlan(state.stops, items, cards, now),
    [state.stops, items, cards, now],
  );

  const handleStart = useCallback(() => {
    const allItems = resolvedPlan.stops.flatMap((s) => s.items);
    void navigate("/review/session", {
      state: { preSeededItems: allItems, planStops: resolvedPlan.stops },
    });
  }, [resolvedPlan, navigate]);

  // ── header ────────────────────────────────────────────────────────────────
  const planSubhead =
    planSummary.stopCount === 0
      ? "No stops — add skills below."
      : `${planSummary.stopCount} ${planSummary.stopCount === 1 ? "stop" : "stops"} · ${planSummary.totalItems} ${planSummary.hasGatedStop ? "examples" : "items"} · ~${planSummary.totalMins} min.`;

  return (
    <div className="max-w-2xl mx-auto py-9 px-8 pb-16">
      <PageHeader
        back={{ label: "← Today", href: "/review" }}
        eyebrow="customize session"
        title="Your plan"
        emphasis="plan"
        subhead={planSubhead}
      />

      {/* ── stop cards ── */}
      <section aria-label="Session stops" className="mb-8 space-y-4">
        {state.stops.map((stop, stopIdx) => (
          <CustomStopCard
            key={stop.kind}
            stop={stop}
            nodes={MVP_NODES}
            focusTracks={planFocusTracks}
            eligibleNewNodes={planEligibleNewNodes}
            onUpdateNode={(nodeIdx, count) => updateNode(stopIdx, nodeIdx, count)}
            onRemoveNode={(nodeIdx) => removeNode(stopIdx, nodeIdx)}
            onAddNode={(nodeId) => addNode(stopIdx, nodeId)}
          />
        ))}
      </section>

      {/* ── footer ── */}
      <div className="border-t border-ink/10 pt-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={handleStart}
            disabled={resolvedPlan.totalItemCount === 0}
            className="flex-1 bg-ink text-paper px-[18px] py-3 font-sans text-sm font-medium hover:bg-accentSoft transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            ▶ Start this plan · ~{planSummary.totalMins} min
          </button>
          <Link
            to="/review"
            className={buttonClasses("secondary") + " text-center"}
          >
            Reset to coach&apos;s plan
          </Link>
        </div>
      </div>
    </div>
  );
}

// ─── CustomStopCard ───────────────────────────────────────────────────────────

function CustomStopCard({
  stop,
  nodes,
  focusTracks,
  eligibleNewNodes,
  onUpdateNode,
  onRemoveNode,
  onAddNode,
}: {
  stop: StopEdit;
  nodes: CurriculumNode[];
  focusTracks: FocusTrack[];
  eligibleNewNodes: CurriculumNode[];
  onUpdateNode: (nodeIdx: number, count: number) => void;
  onRemoveNode: (nodeIdx: number) => void;
  onAddNode: (nodeId: string) => void;
}): ReactNode {
  const [addNodeId, setAddNodeId] = useState("");

  const existingNodeIds = new Set(stop.nodes.map((n) => n.nodeId));
  const gatedNodes = stop.nodeAcquisition?.filter((a) => a.status === "needs-listen") ?? [];
  const listenRemaining = gatedNodes.reduce((s, a) => s + Math.max(0, a.threshold - a.exposures), 0);
  const totalItems = stop.nodes.reduce((s, n) => s + n.itemCount, 0);
  const totalMins = Math.max(1, Math.round(totalItems / 6));

  const availableForAdd =
    stop.kind === "introduce"
      ? eligibleNewNodes.filter((n) => !existingNodeIds.has(n.id))
      : focusTracks.flatMap((t) => t.nodes).filter((n) => !existingNodeIds.has(n.id));

  const handleAdd = () => {
    if (!addNodeId) return;
    onAddNode(addNodeId);
    setAddNodeId("");
  };

  // ── empty state ────────────────────────────────────────────────────────────
  if (stop.nodes.length === 0) {
    return (
      <div
        className={`border border-ink/10 ${stopAttr(STOP_LEFT_RULE, stop.kind, "border-l-4 border-l-inkFaded")}`}
        aria-label={`${stopAttr(STOP_LABELS, stop.kind, stop.kind.toUpperCase())}, empty`}
      >
        <div className="flex items-center gap-3 px-4 py-3">
          <span
            className={`shrink-0 w-2 h-2 rounded-full opacity-30 ${stopAttr(STOP_DOT_BG, stop.kind, "bg-inkFaded")}`}
            aria-hidden="true"
          />
          <span className={`font-mono text-[10px] uppercase tracking-[0.18em] font-medium opacity-40 ${stopAttr(STOP_COLORS, stop.kind, "text-inkSoft")}`}>
            {stopAttr(STOP_LABELS, stop.kind, stop.kind.toUpperCase())}
          </span>
          <span className="font-serif italic text-sm text-inkFaded">
            — {stopAttr(STOP_EMPTY_REASON, stop.kind, "")}
          </span>
        </div>
        {availableForAdd.length > 0 && (
          <AddNodeRow
            stop={stop}
            focusTracks={focusTracks}
            availableForAdd={availableForAdd}
            addNodeId={addNodeId}
            onChangeAddNodeId={setAddNodeId}
            onAdd={handleAdd}
          />
        )}
      </div>
    );
  }

  // ── populated state ────────────────────────────────────────────────────────
  return (
    <div
      className={`bg-paperWarm border border-ink/10 ${stopAttr(STOP_LEFT_RULE, stop.kind, "border-l-4 border-l-inkFaded")}`}
      aria-label={stopAttr(STOP_LABELS, stop.kind, stop.kind.toUpperCase())}
    >
      {/* header */}
      <div className="flex items-center gap-3 px-4 pt-4 pb-2">
        <span
          className={`shrink-0 w-2 h-2 rounded-full ${stopAttr(STOP_DOT_BG, stop.kind, "bg-inkFaded")}`}
          aria-hidden="true"
        />
        <span className={`font-mono text-[10px] uppercase tracking-[0.18em] font-medium ${stopAttr(STOP_COLORS, stop.kind, "text-inkSoft")}`}>
          {stopAttr(STOP_LABELS, stop.kind, stop.kind.toUpperCase())}
        </span>
        <span className="font-mono text-[10px] text-inkFaded ml-auto tabular-nums">
          {gatedNodes.length > 0
            ? `${listenRemaining} examples · ~${Math.max(1, Math.ceil(listenRemaining / 6))} min`
            : `${totalItems} items · ~${totalMins} min`}
        </span>
      </div>

      {/* reason */}
      {stop.reason && (
        <p className="font-serif italic text-sm text-inkSoft leading-snug px-4 pb-3 pl-9">
          {stop.reason}
        </p>
      )}

      {/* node rows */}
      <div className="border-t border-ink/10">
        {stop.nodes.map((nodeEdit, nodeIdx) => {
          const nodeTitle = nodes.find((n) => n.id === nodeEdit.nodeId)?.title ?? nodeEdit.nodeId;
          const acq = stop.nodeAcquisition?.find((a) => a.nodeId === nodeEdit.nodeId);
          const isGated = acq?.status === "needs-listen";
          const fillPct = acq ? Math.min(100, Math.round((acq.exposures / acq.threshold) * 100)) : 0;
          const baseListenRoute = stop.kind === "introduce" ? nodeListenRoute(nodeEdit.nodeId) : null;
          const listenRoute = baseListenRoute && isGated
            ? `${baseListenRoute}&coached=true&itemCount=${Math.max(1, acq!.threshold - acq!.exposures)}&from=today`
            : baseListenRoute ? `${baseListenRoute}&from=today` : null;

          return (
            <div
              key={nodeEdit.nodeId}
              className="flex items-start gap-3 px-4 py-3 pl-9 border-b border-ink/10 last:border-b-0"
            >
              <div className="flex flex-col gap-1.5 flex-1 min-w-0">
                {/* title + action links */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-serif text-sm text-ink leading-snug">{nodeTitle}</span>
                  {listenRoute && (
                    <Link
                      to={listenRoute}
                      className="font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 bg-info text-paper hover:opacity-90 transition-opacity shrink-0"
                    >
                      Listen →
                    </Link>
                  )}
                  {hasLessonForNode(nodeEdit.nodeId) && (
                    <Link
                      to={`/learn/${nodeEdit.nodeId}`}
                      className="font-mono text-[9px] uppercase tracking-widest text-inkFaded hover:text-accent transition-colors shrink-0"
                    >
                      Read →
                    </Link>
                  )}
                </div>
                {/* exposure bar for gated INTRODUCE nodes */}
                {isGated && acq && (
                  <div className="flex items-center gap-2">
                    <div className="h-0.5 bg-ink/10 overflow-hidden flex-1">
                      <div className="h-full bg-success" style={{ width: `${fillPct}%` }} />
                    </div>
                    <span className="font-mono text-[9px] text-inkFaded tabular-nums shrink-0">
                      {acq.exposures}/{acq.threshold}
                    </span>
                  </div>
                )}
              </div>

              {/* controls */}
              <div className="flex items-center gap-1 shrink-0 mt-0.5">
                {!isGated && (
                  <>
                    <button
                      type="button"
                      onClick={() => onUpdateNode(nodeIdx, nodeEdit.itemCount - 1)}
                      className="w-7 h-7 flex items-center justify-center border border-ink/20 text-inkFaded hover:text-ink hover:border-ink/40 transition-colors font-mono text-sm"
                      aria-label={`Decrease ${nodeTitle} items`}
                    >
                      −
                    </button>
                    <NodeCountInput
                      value={nodeEdit.itemCount}
                      onChange={(n) => onUpdateNode(nodeIdx, n)}
                      label={`${nodeTitle} item count`}
                    />
                    <button
                      type="button"
                      onClick={() => onUpdateNode(nodeIdx, nodeEdit.itemCount + 1)}
                      className="w-7 h-7 flex items-center justify-center border border-ink/20 text-inkFaded hover:text-ink hover:border-ink/40 transition-colors font-mono text-sm"
                      aria-label={`Increase ${nodeTitle} items`}
                    >
                      +
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => onRemoveNode(nodeIdx)}
                  className="w-7 h-7 flex items-center justify-center border border-ink/20 text-inkFaded hover:text-accent hover:border-accent/40 transition-colors font-mono text-xs ml-1"
                  aria-label={`Remove ${nodeTitle} from this stop`}
                >
                  ✕
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* add node row */}
      {availableForAdd.length > 0 && (
        <AddNodeRow
          stop={stop}
          focusTracks={focusTracks}
          availableForAdd={availableForAdd}
          addNodeId={addNodeId}
          onChangeAddNodeId={setAddNodeId}
          onAdd={handleAdd}
        />
      )}
    </div>
  );
}

// ─── AddNodeRow ───────────────────────────────────────────────────────────────

function AddNodeRow({
  stop,
  focusTracks,
  availableForAdd,
  addNodeId,
  onChangeAddNodeId,
  onAdd,
}: {
  stop: StopEdit;
  focusTracks: FocusTrack[];
  availableForAdd: CurriculumNode[];
  addNodeId: string;
  onChangeAddNodeId: (id: string) => void;
  onAdd: () => void;
}): ReactNode {
  return (
    <div className="flex gap-2 items-center px-4 py-3 pl-9 border-t border-ink/10">
      <select
        value={addNodeId}
        onChange={(e) => onChangeAddNodeId(e.target.value)}
        className="flex-1 px-2 py-1.5 border border-ink/20 bg-paper font-mono text-[11px] focus:outline-none focus:border-ink"
        aria-label={`Add a skill to ${stopAttr(STOP_LABELS, stop.kind, stop.kind.toUpperCase())}`}
      >
        <option value="">+ Add a skill…</option>
        {stop.kind === "introduce"
          ? availableForAdd.map((n) => (
              <option key={n.id} value={n.id}>{n.title}</option>
            ))
          : focusTracks.map((t) => {
              const trackNodes = t.nodes.filter((n) => !new Set(stop.nodes.map((e) => e.nodeId)).has(n.id));
              if (trackNodes.length === 0) return null;
              return (
                <optgroup key={t.track} label={t.label}>
                  {trackNodes.map((n) => (
                    <option key={n.id} value={n.id}>{n.title}</option>
                  ))}
                </optgroup>
              );
            })}
      </select>
      <button
        type="button"
        onClick={onAdd}
        disabled={!addNodeId}
        className={`${buttonClasses("text")} disabled:opacity-40 disabled:cursor-not-allowed`}
      >
        Add →
      </button>
    </div>
  );
}

// ─── NodeCountInput ───────────────────────────────────────────────────────────

function NodeCountInput({
  value,
  onChange,
  label,
}: {
  value: number;
  onChange: (n: number) => void;
  label: string;
}) {
  const [raw, setRaw] = useState(String(value));

  useEffect(() => { setRaw(String(value)); }, [value]);

  function commit() {
    const parsed = parseInt(raw, 10);
    if (!isNaN(parsed) && parsed >= 1) {
      onChange(parsed);
    } else {
      setRaw(String(value));
    }
  }

  return (
    <input
      type="text"
      inputMode="numeric"
      value={raw}
      onChange={(e) => setRaw(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") { setRaw(String(value)); e.currentTarget.blur(); }
      }}
      aria-label={label}
      className="font-mono text-sm w-8 h-7 text-center tabular-nums border border-ink/20 bg-white focus:outline-none focus:border-ink"
    />
  );
}
